import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LogoutIcon from "@mui/icons-material/Logout";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { dropinApi, isApiError } from "@bw/shared/api";
import { useCourtStateStream, useMinuteNow } from "@bw/shared/hooks";
import type {
  DropInActiveSessionPage,
  DropInSessionResponse,
} from "@bw/shared/types/generated";
import { formatDropInSessionExpiry } from "@bw/shared/utils";

import {
  listQueueRecoveryReasons,
  type QueueRecoveryReason,
} from "@/libs/facilityApi";
import {
  StaffDropInCancellationDialog,
  type StaffDropInCancellationTarget,
} from "@/components/drop-in/staff-drop-in-cancellation-dialog";

/**
 * CheckedInRoster — a staff "who is checked in right now" roster for the Drop-in
 * service home Dashboard tab. The FIRST business consumer of the (previously
 * orphaned) staff-only endpoint GET /facilities/{id}/drop-in/sessions via
 * dropinApi.listActiveSessions.
 *
 * Thin presentation layer: it renders exactly what the backend returns — resolved
 * display name (never a raw user_id — no-UID-in-UI), payment method, authoritative
 * session expiry, and checked-in time — in SERVER order (no client re-sort).
 * Search covers the full result set; fixed 50-row pages and a bounded viewport
 * keep the operator page stable at hundreds of same-day sessions. The facility
 * court-state stream reconciles it after check-in and expiry mutations.
 *
 * It deliberately uses listActiveSessions, NOT listCheckedInPlayers — the latter is
 * player-gated and privacy-filtered (built for the player-side party-invite picker),
 * so it is not usable as a staff roster.
 */

const PAGE_SIZE = 50;

export interface CheckedInRosterProps {
  facilityId: string;
  timeZone: string;
  canEndAdmission?: boolean;
  canCancelAdmission?: boolean;
  onAdmissionStateChanged?: () => void;
}

type EndAdmissionTarget = {
  session: DropInSessionResponse;
  safePlayerLabel: string;
};

// Resolve a safe display name or null. Guards the no-UID-in-UI rule: a null/blank
// name, or a name that equals the raw user_id (the resolver's own fallback), is
// treated as unresolved → the UI shows an em-dash, never the opaque uid.
function resolvedName(session: DropInSessionResponse): string | null {
  const name = session.display_name;
  if (!name || name.trim() === "") return null;
  if (name === session.user_id) return null;
  return name;
}

function titleCase(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatCheckedIn(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  } catch {
    return "—";
  }
}

export function CheckedInRoster({
  facilityId,
  timeZone,
  canEndAdmission = false,
  canCancelAdmission = false,
  onAdmissionStateChanged,
}: CheckedInRosterProps) {
  return (
    <FacilityCheckedInRoster
      key={facilityId}
      facilityId={facilityId}
      timeZone={timeZone}
      canEndAdmission={canEndAdmission}
      canCancelAdmission={canCancelAdmission}
      onAdmissionStateChanged={onAdmissionStateChanged}
    />
  );
}

function FacilityCheckedInRoster({
  facilityId,
  timeZone,
  canEndAdmission = false,
  canCancelAdmission = false,
  onAdmissionStateChanged,
}: CheckedInRosterProps) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([
    undefined,
  ]);
  const [endTarget, setEndTarget] = useState<EndAdmissionTarget | null>(null);
  const [cancelTarget, setCancelTarget] =
    useState<StaffDropInCancellationTarget | null>(null);
  const [reasonCode, setReasonCode] = useState("");
  const [privateNote, setPrivateNote] = useState("");
  const [endError, setEndError] = useState<string | null>(null);
  const [rosterSyncNotice, setRosterSyncNotice] = useState<string | null>(null);
  const [knownInactiveSessionIds, setKnownInactiveSessionIds] = useState<
    Set<string>
  >(() => new Set());
  const [ending, setEnding] = useState(false);
  const endingRef = useRef(false);
  const aliveRef = useRef(true);
  const now = useMinuteNow();
  const viewportRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const endDialogContentRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const rosterQueryPrefix = useMemo(
    () => ["drop-in", "active-sessions", facilityId] as const,
    [facilityId],
  );
  const cursor = cursorStack.at(-1);
  const pageNumber = cursorStack.length;
  const rosterQueryKey = useMemo(
    () => [...rosterQueryPrefix, debouncedSearch, cursor] as const,
    [cursor, debouncedSearch, rosterQueryPrefix],
  );

  const rosterQuery = useQuery<DropInActiveSessionPage>({
    queryKey: rosterQueryKey,
    queryFn: () =>
      dropinApi.listActiveSessions(facilityId, {
        q: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        cursor,
      }),
    enabled: Boolean(facilityId),
  });
  const recoveryReasonsQuery = useQuery<QueueRecoveryReason[]>({
    queryKey: ["queue-recovery-reasons", facilityId],
    queryFn: () => listQueueRecoveryReasons(facilityId),
    enabled: Boolean(facilityId) && canEndAdmission && endTarget !== null,
  });
  const reconcileRoster = useCallback(() => {
    return queryClient.invalidateQueries(
      {
        queryKey: rosterQueryPrefix,
        refetchType: "active",
      },
      { throwOnError: true },
    );
  }, [queryClient, rosterQueryPrefix]);
  useCourtStateStream({
    facilityId,
    revision: 0,
    onReconcile: reconcileRoster,
    enabled: Boolean(facilityId),
  });
  const sessions = (rosterQuery.data?.items ?? []).slice(0, PAGE_SIZE);
  const endAdmissionReasons = useMemo(
    () =>
      (recoveryReasonsQuery.data ?? []).filter((reason) =>
        reason.actions.includes("end_admission"),
      ),
    [recoveryReasonsQuery.data],
  );
  const nextCursor = rosterQuery.data?.next_cursor ?? null;
  const loading = rosterQuery.isPending;
  const hasRosterSnapshot = rosterQuery.data !== undefined;
  const error = rosterQuery.isError
    ? rosterQuery.error instanceof Error
      ? rosterQuery.error.message
      : "Could not load the checked-in roster."
    : null;

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase();
    if (normalizedSearch === debouncedSearch) return;
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(normalizedSearch);
      setCursorStack([undefined]);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [debouncedSearch, search]);

  useEffect(() => {
    if (viewportRef.current) viewportRef.current.scrollTop = 0;
  }, [debouncedSearch, pageNumber]);

  useEffect(() => {
    if (
      reasonCode &&
      !endAdmissionReasons.some((reason) => reason.code === reasonCode)
    ) {
      setReasonCode("");
      setEndError(null);
    }
  }, [endAdmissionReasons, reasonCode]);

  useEffect(() => {
    if (
      endTarget &&
      !reasonCode &&
      recoveryReasonsQuery.isSuccess &&
      endAdmissionReasons.length > 0
    ) {
      endDialogContentRef.current
        ?.querySelector<HTMLElement>("[role=combobox][aria-label='Reason']")
        ?.focus();
    }
  }, [
    endAdmissionReasons.length,
    endTarget,
    reasonCode,
    recoveryReasonsQuery.isSuccess,
  ]);

  useEffect(() => {
    if (rosterQuery.isSuccess && !rosterQuery.isFetching) {
      setRosterSyncNotice(null);
    }
  }, [rosterQuery.isFetching, rosterQuery.isSuccess]);

  useEffect(() => {
    if (canEndAdmission || !endTarget || ending) return;
    setEndTarget(null);
    setReasonCode("");
    setPrivateNote("");
    setEndError(null);
  }, [canEndAdmission, endTarget, ending]);

  const resultCount = `${sessions.length} ${
    sessions.length === 1 ? "player" : "players"
  } on this page`;
  const showPagination = sessions.length > 0 || pageNumber > 1;
  const pageStatus = error
    ? "Page unavailable"
    : loading
      ? "Loading page"
      : resultCount;

  function showNextPage() {
    if (!nextCursor) return;
    setCursorStack((current) => [...current, nextCursor]);
  }

  function showPreviousPage() {
    setCursorStack((current) =>
      current.length > 1 ? current.slice(0, -1) : current,
    );
  }

  function openEndAdmission(
    session: DropInSessionResponse,
    safePlayerLabel: string,
  ) {
    if (!canEndAdmission) return;
    setEndTarget({ session, safePlayerLabel });
    setReasonCode("");
    setPrivateNote("");
    setEndError(null);
  }

  function openCancellation(
    session: DropInSessionResponse,
    safePlayerLabel: string,
  ) {
    if (!canCancelAdmission) return;
    setCancelTarget({ session, safePlayerLabel });
  }

  function clearEndAdmission() {
    setEndTarget(null);
    setReasonCode("");
    setPrivateNote("");
    setEndError(null);
  }

  function closeEndAdmission() {
    if (endingRef.current) return;
    clearEndAdmission();
  }

  function markSessionInactive(sessionId: string) {
    setKnownInactiveSessionIds((current) => {
      if (current.has(sessionId)) return current;
      const next = new Set(current);
      next.add(sessionId);
      return next;
    });
  }

  async function reconcileTarget(
    sessionId: string,
  ): Promise<"active" | "inactive" | "failed" | "obsolete"> {
    try {
      await reconcileRoster();
    } catch {
      return aliveRef.current ? "failed" : "obsolete";
    }
    if (!aliveRef.current) return "obsolete";
    const canonicalPage =
      queryClient.getQueryData<DropInActiveSessionPage>(rosterQueryKey);
    if (!canonicalPage?.items) return "failed";
    return canonicalPage.items.some(
      (session) => session.session_id === sessionId,
    )
      ? "active"
      : "inactive";
  }

  async function endAdmission() {
    if (!canEndAdmission || !endTarget || !reasonCode || endingRef.current)
      return;
    const target = endTarget;
    endingRef.current = true;
    setEnding(true);
    setEndError(null);
    setRosterSyncNotice(null);
    try {
      try {
        await dropinApi.endAdmission(facilityId, target.session.session_id, {
          reason_code: reasonCode,
          note: privateNote.trim() || null,
        });
      } catch (cause) {
        if (!aliveRef.current) return;
        const staleConflict =
          isApiError(cause) &&
          cause.status === 409 &&
          cause.code === "DROPIN_SESSION_NOT_ACTIVE";
        const cancellationRecoveryPending =
          isApiError(cause) &&
          cause.code === "DROPIN_CANCELLATION_RECOVERY_PENDING";
        if (cancellationRecoveryPending) {
          clearEndAdmission();
          onAdmissionStateChanged?.();
          const outcome = await reconcileTarget(target.session.session_id);
          if (outcome === "failed" && aliveRef.current) {
            setRosterSyncNotice(
              "Cancellation recovery is pending, but the checked-in roster could not be refreshed. This row may be out of date.",
            );
          }
          return;
        }
        if (staleConflict) {
          markSessionInactive(target.session.session_id);
          clearEndAdmission();
          onAdmissionStateChanged?.();
          const outcome = await reconcileTarget(target.session.session_id);
          if (outcome === "failed" && aliveRef.current) {
            setRosterSyncNotice(
              "Admission was already inactive, but the checked-in roster could not be refreshed. This row may be out of date.",
            );
          }
          return;
        }

        const commandError =
          cause instanceof Error
            ? cause.message
            : "Could not end this admission. Refresh and try again.";
        setEndError(commandError);
        return;
      }

      if (!aliveRef.current) return;
      markSessionInactive(target.session.session_id);
      clearEndAdmission();
      onAdmissionStateChanged?.();
      const outcome = await reconcileTarget(target.session.session_id);
      if (!aliveRef.current || outcome === "obsolete") return;
      if (outcome === "failed") {
        setRosterSyncNotice(
          "Admission ended, but the checked-in roster could not be refreshed. This row may be out of date.",
        );
      } else if (outcome === "active") {
        setRosterSyncNotice(
          "Admission ended, but the checked-in roster has not updated yet. This row may be out of date.",
        );
      }
    } finally {
      endingRef.current = false;
      if (aliveRef.current) setEnding(false);
    }
  }

  async function completeCancellation(sessionId: string) {
    markSessionInactive(sessionId);
    setCancelTarget(null);
    onAdmissionStateChanged?.();
    const outcome = await reconcileTarget(sessionId);
    if (!aliveRef.current || outcome === "obsolete") return;
    if (outcome === "failed") {
      setRosterSyncNotice(
        "Admission was cancelled, but the checked-in roster could not be refreshed. This row may be out of date.",
      );
    } else if (outcome === "active") {
      setRosterSyncNotice(
        "Admission was cancelled, but the checked-in roster has not updated yet. This row may be out of date.",
      );
    }
  }

  async function reconcileCancellationTarget(sessionId: string) {
    const outcome = await reconcileTarget(sessionId);
    if (outcome === "failed" && aliveRef.current) {
      setRosterSyncNotice(
        "Cancellation status could not be refreshed. This row may be out of date.",
      );
    }
  }

  return (
    <Paper component="section" variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{
            alignItems: { sm: "center" },
            justifyContent: "space-between",
          }}
        >
          <Typography component="h2" variant="h6">
            Current admissions
          </Typography>
          <TextField
            inputRef={searchInputRef}
            type="search"
            placeholder="Search players"
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            slotProps={{
              htmlInput: {
                maxLength: 100,
                role: "searchbox",
                "aria-label": "Search checked-in players",
              },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" aria-hidden="true" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: { xs: "100%", sm: 288 } }}
          />
        </Stack>

        {rosterSyncNotice ? (
          <Alert severity="info" role="status">
            {rosterSyncNotice}
          </Alert>
        ) : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        {!loading && sessions.length > 0 ? (
          <TableContainer
            ref={viewportRef}
            data-testid="roster-table-viewport"
            sx={{
              maxHeight: 320,
              overflowY: "auto",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={(theme) => ({
                tableLayout: "fixed",
                [theme.breakpoints.up("sm")]: { tableLayout: "auto" },
              })}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Player</TableCell>
                  <TableCell
                    sx={(theme) => ({
                      display: "none",
                      [theme.breakpoints.up("md")]: { display: "table-cell" },
                    })}
                  >
                    Payment
                  </TableCell>
                  <TableCell
                    sx={(theme) => ({
                      display: "none",
                      [theme.breakpoints.up("sm")]: { display: "table-cell" },
                    })}
                  >
                    Session ends
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={(theme) => ({
                      width: 96,
                      textAlign: "right",
                      [theme.breakpoints.up("sm")]: {
                        width: "auto",
                        textAlign: "left",
                      },
                    })}
                  >
                    Checked in
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((session, sessionIndex) => {
                  const name = resolvedName(session);
                  const safePlayerLabel =
                    name ??
                    `unresolved player on roster row ${sessionIndex + 1}`;
                  const knownInactive = knownInactiveSessionIds.has(
                    session.session_id,
                  );
                  const cancellationRecoveryPending =
                    session.cancellation_recovery_pending === true;
                  const expiryLabel = formatDropInSessionExpiry(
                    session.expires_at,
                    {
                      now,
                      timeZone,
                    },
                  );
                  return (
                    <TableRow key={session.session_id} data-testid="roster-row">
                      <TableCell sx={{ minWidth: 0, whiteSpace: "normal" }}>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ minWidth: 0, alignItems: "flex-start" }}
                        >
                          <Typography
                            component="span"
                            data-testid="roster-player-name"
                            variant="body2"
                            sx={{
                              display: "block",
                              minWidth: 0,
                              flex: 1,
                              overflowWrap: "anywhere",
                              fontWeight: 600,
                            }}
                          >
                            {name ?? (
                              <Box
                                component="span"
                                sx={{ color: "text.secondary" }}
                              >
                                —
                              </Box>
                            )}
                          </Typography>
                          {cancellationRecoveryPending ? (
                            <Chip
                              label="Cancellation pending"
                              size="small"
                              variant="outlined"
                              sx={{ flexShrink: 0 }}
                            />
                          ) : null}
                          {canCancelAdmission || canEndAdmission ? (
                            <Stack
                              direction="row"
                              spacing={0}
                              sx={{ flexShrink: 0 }}
                            >
                              {canCancelAdmission ? (
                                <IconButton
                                  size="small"
                                  color="default"
                                  aria-label={
                                    knownInactive
                                      ? `Session inactive for ${safePlayerLabel}`
                                      : cancellationRecoveryPending
                                        ? `Review cancellation for ${safePlayerLabel}`
                                        : `Cancel session for ${safePlayerLabel}`
                                  }
                                  title={
                                    cancellationRecoveryPending
                                      ? "Review cancellation"
                                      : "Cancel and apply policy"
                                  }
                                  disabled={knownInactive}
                                  onClick={() =>
                                    openCancellation(session, safePlayerLabel)
                                  }
                                >
                                  <PriceCheckIcon fontSize="small" />
                                </IconButton>
                              ) : null}
                              {canEndAdmission &&
                              !cancellationRecoveryPending ? (
                                <IconButton
                                  size="small"
                                  color="default"
                                  aria-label={
                                    knownInactive
                                      ? `Admission ended for ${safePlayerLabel}`
                                      : `End admission for ${safePlayerLabel}`
                                  }
                                  title={
                                    knownInactive
                                      ? "Admission ended"
                                      : "End admission"
                                  }
                                  disabled={knownInactive}
                                  onClick={() =>
                                    openEndAdmission(session, safePlayerLabel)
                                  }
                                >
                                  <LogoutIcon fontSize="small" />
                                </IconButton>
                              ) : null}
                            </Stack>
                          ) : null}
                        </Stack>
                        {expiryLabel ? (
                          <Typography
                            component="time"
                            dateTime={session.expires_at ?? undefined}
                            variant="caption"
                            color="text.secondary"
                            sx={(theme) => ({
                              mt: 0.5,
                              display: "block",
                              whiteSpace: "normal",
                              lineHeight: 1.25,
                              [theme.breakpoints.up("sm")]: {
                                display: "none",
                              },
                            })}
                          >
                            {expiryLabel}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell
                        sx={(theme) => ({
                          display: "none",
                          [theme.breakpoints.up("md")]: {
                            display: "table-cell",
                          },
                        })}
                      >
                        {titleCase(session.payment_method)}
                      </TableCell>
                      <TableCell
                        sx={(theme) => ({
                          display: "none",
                          [theme.breakpoints.up("sm")]: {
                            display: "table-cell",
                          },
                        })}
                      >
                        <time dateTime={session.expires_at ?? undefined}>
                          {expiryLabel ?? "—"}
                        </time>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={(theme) => ({
                          width: 96,
                          whiteSpace: "nowrap",
                          color: "text.secondary",
                          textAlign: "right",
                          [theme.breakpoints.up("sm")]: {
                            width: "auto",
                            textAlign: "left",
                          },
                        })}
                      >
                        {formatCheckedIn(session.checked_in_at, timeZone)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : error && !hasRosterSnapshot ? null : (
          <Typography
            role="status"
            variant="body2"
            color="text.secondary"
            sx={{ py: 4, textAlign: "center" }}
          >
            {loading
              ? "Loading…"
              : pageNumber > 1
                ? "No players on this page."
                : debouncedSearch
                  ? "No checked-in players match."
                  : "No one is checked in right now."}
          </Typography>
        )}
        {showPagination ? (
          <Stack
            direction="row"
            spacing={1.5}
            aria-live="polite"
            sx={{
              minHeight: 32,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {pageStatus}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <IconButton
                size="small"
                aria-label="Previous roster page"
                title="Previous page"
                disabled={pageNumber === 1 || rosterQuery.isFetching}
                onClick={showPreviousPage}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" color="text.secondary">
                Page {pageNumber}
              </Typography>
              <IconButton
                size="small"
                aria-label="Next roster page"
                title="Next page"
                disabled={!nextCursor || rosterQuery.isFetching}
                onClick={showNextPage}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        ) : null}
      </Stack>

      <Dialog
        open={endTarget !== null}
        onClose={closeEndAdmission}
        disableRestoreFocus
        fullWidth
        maxWidth="sm"
        slotProps={{
          transition: {
            onExited: () => searchInputRef.current?.focus(),
          },
          paper: {
            sx: { maxHeight: "calc(100dvh - 2rem)" },
          },
        }}
      >
        <DialogTitle>End drop-in admission?</DialogTitle>
        <DialogContent ref={endDialogContentRef} dividers>
          <Stack spacing={2}>
            <DialogContentText>
              Play access will end immediately. Payment and any membership or
              package entitlement remain unchanged.
            </DialogContentText>
            {endTarget ? (
              <Box
                component="dl"
                sx={{
                  m: 0,
                  display: "grid",
                  gridTemplateColumns: "5rem minmax(0, 1fr)",
                  columnGap: 1.5,
                  rowGap: 0.5,
                }}
              >
                <Typography
                  component="dt"
                  variant="body2"
                  color="text.secondary"
                >
                  Player
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  sx={{ m: 0, overflowWrap: "anywhere", fontWeight: 600 }}
                >
                  {endTarget.safePlayerLabel}
                </Typography>
                <Typography
                  component="dt"
                  variant="body2"
                  color="text.secondary"
                >
                  Checked in
                </Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                  {formatCheckedIn(endTarget.session.checked_in_at, timeZone)}
                </Typography>
              </Box>
            ) : null}
            <FormControl fullWidth required>
              <InputLabel id="end-admission-reason-label" shrink>
                Reason
              </InputLabel>
              <Select
                labelId="end-admission-reason-label"
                id="end-admission-reason"
                aria-label="Reason"
                aria-required="true"
                label="Reason"
                value={reasonCode}
                onChange={(event) => {
                  setReasonCode(event.target.value);
                  setEndError(null);
                }}
                disabled={ending || recoveryReasonsQuery.isPending}
                displayEmpty
                renderValue={(value) =>
                  value
                    ? (endAdmissionReasons.find(
                        (reason) => reason.code === value,
                      )?.label ?? value)
                    : "Choose a reason"
                }
              >
                {endAdmissionReasons.map((reason) => (
                  <MenuItem key={reason.reason_id} value={reason.code}>
                    {reason.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              id="end-admission-note"
              label="Private note"
              value={privateNote}
              multiline
              rows={3}
              placeholder="Optional staff-only context"
              disabled={ending}
              onChange={(event) => setPrivateNote(event.target.value)}
              helperText="This note is recorded for staff and is not shown to the player."
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
            {recoveryReasonsQuery.isError || endError ? (
              <Alert severity="error">
                {endError ??
                  "Could not load admission reasons. Refresh and try again."}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            ref={cancelButtonRef}
            type="button"
            size="small"
            variant="outlined"
            autoFocus
            onClick={closeEndAdmission}
            disabled={ending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="small"
            variant="contained"
            color="error"
            aria-label="End admission"
            onClick={() => void endAdmission()}
            disabled={
              ending ||
              !canEndAdmission ||
              !reasonCode ||
              recoveryReasonsQuery.isPending ||
              recoveryReasonsQuery.isError
            }
          >
            {ending ? "Ending…" : "End admission"}
          </Button>
        </DialogActions>
      </Dialog>
      {cancelTarget ? (
        <StaffDropInCancellationDialog
          key={cancelTarget.session.session_id}
          facilityId={facilityId}
          target={cancelTarget}
          canCancel={canCancelAdmission}
          onCancelled={(result) => completeCancellation(result.session_id)}
          onReconcile={() =>
            reconcileCancellationTarget(cancelTarget.session.session_id)
          }
          onClose={() => setCancelTarget(null)}
          onCloseAutoFocus={() => searchInputRef.current?.focus()}
        />
      ) : null}
    </Paper>
  );
}
